import { ItemTitle } from '@sleepstation/components'
import { ApiContext } from '@sleepstation/components-context'
import { FormCard, FormWrapper } from '@sleepstation/components-form'
import { AccessRequestForm } from '@sleepstation/components-forms-wellbeing'
import { LAYOUT_ALIGN } from '@sleepstation/const'
import { concat, pick } from 'ramda'
import { isNotEmpty, renameKeysWith } from 'ramda-adjunct'
import React, { useContext } from 'react'
import { useLoaderData, useNavigate } from 'react-router-dom'
import authenticationService from '../../services/authenticationService'
import localStorageService from '../../services/localStorageService'
import portalLandingStarscapes from '../../svg/starscapes/portalLandingStarscapes'
import PortalPage from '../pages/PortalPage'
import FancyListTitled from '../shared/FancyListTitled'
import BreakoutModuleNarrow from '../shared/breakoutModules/BreakoutModuleNarrow'
import HeroBreakoutModule from '../shared/breakoutModules/HeroBreakoutModule'
import CorporateAccessHero from '../shared/heroes/CorporateAccessHero'
import TestimonialSimple from '../shared/testimonials/TestimonialSimple'

// -----------------------------------------------------------------------------
// Const
// -----------------------------------------------------------------------------

// Currently only the following fields - 'key' (passcode) and 'recaptcha' are output from the forms. White list tested using 'key'
const WHITE_LISTED_KEYS = []

// -----------------------------------------------------------------------------
// Utils
// -----------------------------------------------------------------------------

const authorizeAndRedirectToDashboard =
  (navigate, successUrl, context) => (response) => {
    const { token } = JSON.parse(response.config.data)
    authenticationService.addAuthorisation(token, context)
    navigate(successUrl)
  }

const buildPayload = (formValues, context) => {
  const prependString = `portal_${context}_`
  return renameKeysWith(concat(prependString), formValues)
}

const uploadToOnboardingApi = (payload, api) => {
  const localStorageApi = localStorageService()
  api.putOnboardingData(payload).then((result) => {
    const onboardingToken = result.data.data.hash
    localStorageApi.setValue(`onboardingToken`, onboardingToken)
  })
  // TODO handle errors here.
}

const savePersonalData =
  (whiteListedFormValues, context, shouldUploadToApi, api) => (result) => {
    if (shouldUploadToApi) {
      const onboardingDataPayload = buildPayload(whiteListedFormValues, context)
      uploadToOnboardingApi(onboardingDataPayload, api)
    }

    return Promise.resolve(result)
  }

const postPasscodeAndThenPostUserDetails =
  (context, api) =>
  (...formValuesAndId) => {
    const formValues = formValuesAndId[0]
    const whiteListedFormValues = pick(WHITE_LISTED_KEYS, formValues)
    const shouldUploadToApi = isNotEmpty(whiteListedFormValues)

    return api
      .postPasscode(context)(...formValuesAndId)
      .then(
        savePersonalData(whiteListedFormValues, context, shouldUploadToApi, api)
      )
  }

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const PortalLandingModule = () => {
  const data = useLoaderData()
  const navigate = useNavigate()
  const api = useContext(ApiContext)

  const {
    portal,
    page: { content },
  } = data

  return (
    <PortalPage
      data={data}
      footerMargin={0}
      shouldFixHeader
      hideBackground
      hidePrimaryNav
    >
      <HeroBreakoutModule
        images={portalLandingStarscapes}
        imageOffsetBottom={0}
      >
        <CorporateAccessHero
          title={portal.heroSignup.title}
          body={portal.heroSignup.body}
          logo={portal.logo.reversed}
          productShot={content.hero.productShot}
          ctaLabel={portal.heroSignup.link.label}
          ctaUrl={portal.heroSignup.link.url}
          logoHeights={portal.heroSignup.logoHeights}
          logoAlignments={[
            LAYOUT_ALIGN.CENTER,
            LAYOUT_ALIGN.CENTER,
            LAYOUT_ALIGN.LEFT,
          ]}
        />
      </HeroBreakoutModule>
      <BreakoutModuleNarrow
        marginV={[1.5]}
        hasGradient={false}
        hasBottomBorder={false}
      >
        <FancyListTitled {...content.accessList} />
      </BreakoutModuleNarrow>
      <BreakoutModuleNarrow
        paddingV={[[0.5, 1.5]]}
        marginV={[
          [0, 0],
          [0, 2],
        ]}
        hasBottomBorder
        hideBorderAtXSmall
      >
        <FormCard
          title={content.form.title}
          id="portal-access-request-form-container"
          TitleComponent={ItemTitle}
        >
          <FormWrapper
            wrappedComponent={AccessRequestForm}
            promise={postPasscodeAndThenPostUserDetails(portal.context, api)}
            onSuccess={authorizeAndRedirectToDashboard(
              navigate,
              portal.helpers.getPath(`/portal/dashboard/`),
              portal.context
            )}
            modalBody={content.modalError.message}
          />
        </FormCard>
      </BreakoutModuleNarrow>
      <BreakoutModuleNarrow
        hasBottomBorder={false}
        hasGradient={false}
        paddingV={[
          [0.5, 2],
          [1, 3],
          [1, 3],
          [0.5, 3],
        ]}
      >
        <TestimonialSimple {...content.testimonial} />
      </BreakoutModuleNarrow>
    </PortalPage>
  )
}

export default PortalLandingModule
